import UiIcon from "../UiIcon";
import { teamCopy } from "./teamCopy";
import { attentionItems, formatScore, formatValue, indicatorCounts, toNumber } from "./teamModel";
import "../../styles/cvi-team.css";

const STATUS_ORDER = ["on_track", "watch", "critical", "no_result", "no_data"];

function ScoreBar({ value, tone = "navy" }) {
    const n = toNumber(value);
    if (n == null) return <span className="cvi-teamdash-bar is-empty" aria-hidden="true" />;
    return <span className={`cvi-teamdash-bar is-${tone}`} aria-hidden="true"><span style={{ width: `${Math.max(0, Math.min(100, n))}%` }} /></span>;
}

function StateChip({ state, c }) {
    return <span className={`cvi-teamdash-chip is-${state}`}><i aria-hidden="true" />{c.state[state]}</span>;
}

// Team summary: Team CVI with its coverage and state, then the team KPIs in equal tiles.
export function TeamSummary({ c, language, unitLabel, periodState, teamCvi, memberCount, attainment, indicatorTotal, indicatorWithData,
    reviews, support, historical, loading, error, onOpenInbox }) {
    const s = c.summary;
    const hasCvi = teamCvi.value != null;
    return <section className="cvi-teamdash-summary" aria-label={s.label}>
        <div className="cvi-teamdash-cvi">
            <div className="cvi-teamdash-cvi-head">
                <span className="cvi-teamdash-label">{s.teamCvi}</span>
                {!loading && !error && <StateChip state={hasCvi ? periodState : "unavailable"} c={c} />}
            </div>
            <div className="cvi-teamdash-cvi-value">
                <strong>{loading ? "…" : formatScore(teamCvi.value, language)}</strong>
                {hasCvi && <small>{s.outOf}</small>}
            </div>
            <ScoreBar value={teamCvi.value} />
            <p className="cvi-teamdash-cvi-note">{error ? s.error : loading ? s.loading : hasCvi
                ? <><span>{s.coverage(teamCvi.count, teamCvi.total)}</span><span>{s.mean}</span></> : s.noResult}</p>
            <span className="cvi-teamdash-unit">{unitLabel}</span>
        </div>
        <dl className="cvi-teamdash-kpis">
            <div className="cvi-teamdash-kpi">
                <dt>{s.members}</dt>
                <dd><strong>{memberCount}</strong><small>{s.assessed(teamCvi.count)}</small></dd>
            </div>
            <div className="cvi-teamdash-kpi">
                <dt>{s.attainment}</dt>
                <dd><strong>{attainment == null ? "—" : `${formatScore(attainment, language, 0)}${language === "fr" ? "\u00a0%" : "%"}`}</strong>
                    <small>{attainment == null && indicatorTotal ? s.attainmentNone : s.attainmentHelp(indicatorWithData, indicatorTotal)}</small></dd>
            </div>
            {!historical && <div className={`cvi-teamdash-kpi${reviews > 0 ? " is-attention" : ""}`}>
                <dt>{s.reviews}</dt>
                <dd><strong>{reviews}</strong>
                    {onOpenInbox ? <button type="button" className="cvi-teamdash-link" onClick={onOpenInbox}>{s.reviewsOpen}<UiIcon name="chevronRight" size={12} /></button> : <small>{s.reviewsHelp}</small>}</dd>
            </div>}
            {!historical && <div className={`cvi-teamdash-kpi${support > 0 ? " is-attention" : ""}`}>
                <dt>{s.support}</dt>
                <dd><strong>{support}</strong><small>{s.supportHelp}</small></dd>
            </div>}
            {historical && <div className="cvi-teamdash-kpi is-note"><dd><small>{s.currentOnly}</small></dd></div>}
        </dl>
    </section>;
}

export function TeamAttention({ c, items = [], onOpenInbox }) {
    const a = c.attention;
    return <section className="cvi-teamdash-attention" aria-label={a.label}>
        <span className="cvi-teamdash-label">{a.label}</span>
        {items.length === 0 ? <span className="cvi-teamdash-muted">{a.none}</span> : <ul>
            {items.map(item => <li key={item.key} className={`is-${item.tone}`}>
                {item.action === "inbox" && onOpenInbox
                    ? <button type="button" onClick={onOpenInbox}><i aria-hidden="true" />{a[item.key](item.count)}</button>
                    : <span><i aria-hidden="true" />{a[item.key](item.count)}</span>}
            </li>)}
        </ul>}
    </section>;
}

export function TeamMembersTable({ c, language, rows = [], periodState, historical, renderAvatar }) {
    const m = c.members;
    return <section className="cvi-teamdash-card" aria-labelledby="cvi-teamdash-members-title">
        <header className="cvi-teamdash-card-head">
            <div><h2 id="cvi-teamdash-members-title">{m.title}</h2><p>{m.subtitle}</p></div>
            <span className="cvi-teamdash-count">{rows.length}</span>
        </header>
        {rows.length === 0 ? <p className="cvi-teamdash-empty">{m.empty}</p> : <div className={`cvi-teamdash-table-wrap${rows.length <= 5 ? " is-small-team" : ""}`}><table className="cvi-teamdash-members">
            <thead><tr>
                <th scope="col">{m.member}</th>
                <th scope="col" className="is-cvi">{m.cvi}</th>
                <th scope="col" className="is-num"><abbr title={m.individualFull}>{m.individual}</abbr></th>
                <th scope="col" className="is-num"><abbr title={m.collectiveFull}>{m.collective}</abbr></th>
                <th scope="col">{m.status}</th>
            </tr></thead>
            <tbody>{rows.map(row => <tr key={row.id}>
                <th scope="row"><div className="cvi-teamdash-person">
                    {renderAvatar?.(row.member)}
                    <span><strong title={row.name}>{row.name}</strong>{row.position && <small title={row.position}>{row.position}</small>}</span>
                </div></th>
                <td className="is-cvi" data-label={m.cvi}><div className="cvi-teamdash-cvi-cell"><strong>{formatScore(row.cvi, language)}</strong><ScoreBar value={row.cvi} /></div></td>
                <td className="is-num" data-label={m.individual} title={m.individualFull}>{formatScore(row.individual, language)}</td>
                <td className="is-num" data-label={m.collective} title={m.collectiveFull}>{formatScore(row.collective, language)}</td>
                <td data-label={m.status}><div className="cvi-teamdash-status">
                    {row.cvi == null ? <span className="cvi-teamdash-chip is-unavailable"><i aria-hidden="true" />{m.noResult}</span> : <StateChip state={periodState} c={c} />}
                    {!historical && row.waiting > 0 && <span className="cvi-teamdash-tag is-warning">{m.waiting(row.waiting)}</span>}
                </div></td>
            </tr>)}</tbody>
        </table></div>}
    </section>;
}

export function TeamIndicatorsList({ c, language, rows = [], historical }) {
    const t = c.indicators;
    const counts = indicatorCounts(rows);
    return <section className="cvi-teamdash-card" aria-labelledby="cvi-teamdash-indicators-title">
        <header className="cvi-teamdash-card-head">
            <div><h2 id="cvi-teamdash-indicators-title">{t.title}</h2><p>{t.subtitle}</p></div>
            <span className="cvi-teamdash-count">{rows.length}</span>
        </header>
        {rows.length > 0 && <ul className="cvi-teamdash-legend" aria-label={t.title}>
            {STATUS_ORDER.filter(k => counts[k] > 0).map(k => <li key={k} className={`is-${k}`}><i aria-hidden="true" />{t.counts[k]} <b>{counts[k]}</b></li>)}
        </ul>}
        {rows.length === 0 ? <p className="cvi-teamdash-empty">{t.empty}</p> : <ul className="cvi-teamdash-indicators">
            {rows.map(row => {
                const detail = [
                    row.approvedCount > 0 ? t.approvedValues(row.approvedCount) : null,
                    !historical && row.awaitingCount > 0 ? t.awaiting(row.awaitingCount) : null,
                    !historical && row.revisionCount > 0 ? t.revision(row.revisionCount) : null,
                    !row.submittedAny && row.achievement == null ? t.notSubmitted : null,
                ].filter(Boolean);
                return <li key={row.id} className={`is-${row.status}`}>
                    <div className="cvi-teamdash-ind-head">
                        <strong title={row.label}>{row.label}</strong>
                        <span className={`cvi-teamdash-ind-status is-${row.status}`}>{t.counts[row.statusKey || row.status]}</span>
                        <span className="cvi-teamdash-ind-score">{row.achievement == null ? "—" : `${formatScore(row.achievement, language, 0)}${language === "fr" ? " %" : "%"}`}</span>
                    </div>
                    {row.achievement != null && <ScoreBar value={row.achievement} tone={row.status} />}
                    {row.achievement == null && row.actual == null && row.target == null
                        ? <details className="cvi-teamdash-ind-more">
                            <summary>{language === "fr" ? "Détails de l’indicateur" : "Indicator details"}</summary>
                            <dl className="cvi-teamdash-ind-facts">
                                {row.weight != null && <div title={t.weightHelp}><dt>{t.weight}</dt><dd>{formatValue(row.weight, "", language)}</dd></div>}
                                {detail.length > 0 && <div className="is-detail"><dd>{detail.join(" · ")}</dd></div>}
                            </dl>
                          </details>
                        : <dl className="cvi-teamdash-ind-facts">
                            <div><dt>{t.actual}</dt><dd>{formatValue(row.actual, row.unit, language)}</dd></div>
                            <div><dt>{t.target}</dt><dd>{formatValue(row.target, row.unit, language)}</dd></div>
                            {row.weight != null && <div title={t.weightHelp}><dt>{t.weight}</dt><dd>{formatValue(row.weight, "", language)}</dd></div>}
                            {detail.length > 0 && <div className="is-detail"><dd>{detail.join(" · ")}</dd></div>}
                          </dl>}
                </li>;
            })}
        </ul>}
    </section>;
}

// Full team section below the shared page header.
export default function TeamDashboardView(props) {
    const { language = "en", historical, rows, indicators, reviews, support, onOpenInbox } = props;
    const c = teamCopy(language);
    const items = attentionItems({ rows, indicators, reviews, support, historical });
    return <div className="cvi-teamdash" data-cvi-i18n="managed" lang={language}>
        {historical && <p className="cvi-teamdash-historical" role="status"><UiIcon name="shield" size={14} />{c.summary.historical}</p>}
        <TeamSummary c={c} {...props} />
        {!props.loading && !props.error && (items.length > 0 || !historical) && <TeamAttention c={c} items={items} onOpenInbox={onOpenInbox} />}
        <div className="cvi-teamdash-grid">
            <TeamMembersTable c={c} language={language} rows={rows} periodState={props.periodState} historical={historical} renderAvatar={props.renderAvatar} />
            <TeamIndicatorsList c={c} language={language} rows={indicators} historical={historical} />
        </div>
    </div>;
}
